import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getTrackingData, submitTrackingRating, type TrackingData } from "@services/api/tracking";
import type { TrackingStep } from "types/ordre";

// ─── Constants ─────────────────────────────────────────────

const COLORIS_TO_PAINT: Record<string, string> = {
  noir: "black", blanc: "white", gris: "grey", bleu: "blue",
  rouge: "red", vert: "green", jaune: "yellow", marron: "brown",
  orange: "orange", autre: "silver",
};

function getVehicleImageUrl(marque?: string, modele?: string, coloris?: string): string | null {
  if (!marque || !modele) return null;
  const make = encodeURIComponent(marque.toLowerCase());
  const model = encodeURIComponent(modele.toLowerCase());
  const paint = COLORIS_TO_PAINT[coloris || ""] || "grey";
  return `https://cdn.imagin.studio/getimage?customer=img&make=${make}&modelFamily=${model}&modelYear=2023&zoomType=fullscreen&angle=0&paintDescription=${paint}`;
}

interface StepConfig {
  key: TrackingStep;
  title: string;
  subtitle: string;
  cta?: { label: string; action: string };
}

const ALL_STEPS: StepConfig[] = [
  {
    key: "carte_grise_manquante",
    title: "Carte grise manquante",
    subtitle: "En attente du dépôt de la carte grise",
    cta: { label: "Déposer ma carte grise", action: "deposit_carte_grise" },
  },
  {
    key: "acompte_manquant",
    title: "Acompte en attente",
    subtitle: "En attente du règlement de l'acompte",
    cta: { label: "Régler mon acompte", action: "pay_acompte" },
  },
  {
    key: "en_attente_depot",
    title: "En attente du véhicule",
    subtitle: "Nous attendons le dépôt de votre véhicule",
  },
  {
    key: "vehicule_depose",
    title: "Véhicule déposé",
    subtitle: "Votre véhicule est en cours d'analyse",
  },
  {
    key: "devis_pret",
    title: "Devis prêt",
    subtitle: "Le devis est disponible pour validation",
    cta: { label: "Voir le devis", action: "view_devis" },
  },
  {
    key: "reparations_en_cours",
    title: "Réparations en cours",
    subtitle: "Les travaux sur votre véhicule sont en cours",
  },
  {
    key: "paiement_valide",
    title: "Paiement validé",
    subtitle: "Réparations en cours",
  },
  {
    key: "travaux_termines",
    title: "Travaux terminés",
    subtitle: "En attente de la date de restitution",
  },
  {
    key: "vehicule_disponible",
    title: "Véhicule disponible",
    subtitle: "Votre véhicule est prêt à être récupéré",
    cta: { label: "Prendre rendez-vous", action: "schedule_pickup" },
  },
  {
    key: "vehicule_restitue",
    title: "Véhicule restitué",
    subtitle: "Merci de votre confiance !",
  },
];

const MOBILITY_OPTIONS = [
  { key: "attente", label: "Je peux attendre sur place (1h30)" },
  { key: "velo", label: "Demander un vélo équipé (gratuit)" },
  { key: "pret", label: "Demander un véhicule de prêt (zone limitée)" },
  { key: "location", label: "Demander un véhicule de location (liberté/payant)" },
];

const TIRE_OPTIONS = [
  { key: "premier_prix", label: "Pneus premier prix" },
  { key: "intermediaires", label: "Pneus intermédiaires" },
  { key: "premium", label: "Pneus premium" },
];

// ─── Component ─────────────────────────────────────────────

type ScreenState = "loading" | "error" | "ready";

export default function TrackingScreen() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<ScreenState>("loading");
  const [data, setData] = useState<TrackingData | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);

  useEffect(() => {
    if (!id) { setErrorMsg("Lien invalide."); setState("error"); return; }
    getTrackingData(id)
      .then((d) => { setData(d); setSelectedRating(d.client_rating || 0); setState("ready"); })
      .catch(() => { setErrorMsg("Impossible de charger le suivi."); setState("error"); });
  }, [id]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state === "error" || !data) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 p-5">
        <div className="text-[40px]">🔍</div>
        <p className="text-[13px] font-medium text-[#A1A1A1] text-center">{errorMsg}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center">
      <div className="w-full max-w-[375px]">
        <TrackingContent
          data={data}
          ordreId={id!}
          hoveredStar={hoveredStar}
          setHoveredStar={setHoveredStar}
          selectedRating={selectedRating}
          setSelectedRating={setSelectedRating}
        />
      </div>
    </div>
  );
}

// ─── Reusable content (also used in preview modal) ──────────

export function TrackingContent({
  data,
  ordreId,
  hoveredStar,
  setHoveredStar,
  selectedRating,
  setSelectedRating,
  previewMode,
}: {
  data: TrackingData;
  ordreId?: string;
  hoveredStar: number;
  setHoveredStar: (v: number) => void;
  selectedRating: number;
  setSelectedRating: (v: number) => void;
  previewMode?: boolean;
}) {
  const steps = ALL_STEPS.filter((s) =>
    s.key !== "carte_grise_manquante" || !data.has_carte_grise
  );

  const currentStepIndex = steps.findIndex((s) => s.key === data.tracking_step);
  const currentStep = steps[currentStepIndex] ?? steps[0];

  const imgUrl = getVehicleImageUrl(data.vehicle_marque, data.vehicle_modele, data.vehicle_coloris);
  const isRestitue = data.tracking_step === "vehicule_restitue";

  const summaryRows = [
    { label: "Demande", value: `#${data.ordre_num}` },
    { label: "Véhicule", value: data.vehicle_plate || "—" },
    { label: "Service", value: data.service_type || "—" },
    { label: "Type", value: data.intervention_type || "—" },
    { label: "Interventions", value: data.interventions?.join(", ") || "—" },
    { label: "Date de dépôt", value: data.deposit_date || "—" },
    { label: "Date de restitution", value: data.restitution_date || "—" },
  ];

  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Header */}
      <div className="flex flex-col gap-[2px]">
        <span className="text-[11px] font-medium leading-4 text-[#A1A1A1]">
          Demande #{data.ordre_num}
        </span>
        <span className="text-[13px] font-medium leading-5 text-[#5C6BFF]">
          Suivi de ma demande
        </span>
      </div>

      {/* Car image */}
      <div className="flex items-center justify-center p-5 border border-[#D9D9D9] rounded-[10px] bg-white h-[156px]">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt="Véhicule"
            className="w-[280px] h-[116px] object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        ) : (
          <div className="text-[48px] text-[#D9D9D9]">🚗</div>
        )}
      </div>

      {/* Progress bar — weighted step segments */}
      <div className="flex flex-col gap-[10px]">
        <div className="flex gap-[3px]">
          {steps.map((step, i) => {
            const done = currentStepIndex > i || isRestitue;
            const isActive = currentStepIndex === i && !isRestitue;
            return (
              <div key={step.key} className="flex-1 h-1 rounded-full overflow-hidden bg-[#E5E5E5]">
                {isActive ? (
                  <div className="h-full rounded-full bg-[#5C6BFF] animate-loading-bar" />
                ) : done ? (
                  <div className="h-full rounded-full bg-[#5C6BFF] w-full" />
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Step info */}
        <div className="flex flex-col">
          <span className="text-[13px] font-medium leading-5 text-[#5C6BFF]">
            {currentStep.title}
          </span>
          <span className="text-[11px] font-medium leading-4 text-[#A1A1A1]">
            {currentStep.subtitle}
          </span>
        </div>
      </div>

      {/* CTA button */}
      {currentStep.cta && !isRestitue && (
        <button
          onClick={() => {
            if (previewMode) return;
            // "Voir le devis" CTA — navigate to sign page if token exists
            if (currentStep.cta!.action === "view_devis" && data.devis_sign_token) {
              window.location.href = `/sign/${data.devis_sign_token}`;
              return;
            }
            alert("Fonctionnalité à venir");
          }}
          className="flex items-center justify-center py-[15px] bg-black text-white font-bold text-[15px] leading-[100%] rounded-[10px] shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:bg-neutral-800 transition-colors"
        >
          {currentStep.cta.label}
        </button>
      )}

      {/* Devis signature CTA — shown when a devis is pending signature */}
      {data.devis_sign_token && data.devis_sign_status === "pending" && currentStep.key !== "devis_pret" && (
        <button
          onClick={() => !previewMode && (window.location.href = `/sign/${data.devis_sign_token}`)}
          className="flex items-center justify-center gap-[10px] py-[15px] bg-[#5C6BFF] text-white font-bold text-[15px] leading-[100%] rounded-[10px] shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:bg-[#4B5AEE] transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
            <path d="M14.5 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V8.5L14.5 3zM14 4l5 5h-5V4zM6 20V5h7v5h5v10H6z" />
            <path d="M9 13h6v1H9zM9 16h4v1H9z" />
          </svg>
          Signer le devis
        </button>
      )}

      {data.devis_sign_status === "signed" && (
        <div className="flex items-center justify-center gap-[10px] py-[12px] bg-[#D1FAE5] rounded-[10px]">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="#065F46">
            <path d="M6.5 11.5L3 8l1-1 2.5 2.5L12 4l1 1-6.5 6.5z" />
          </svg>
          <span className="font-medium text-[13px] text-[#065F46]">Devis signé</span>
        </div>
      )}

      {/* Star rating (only on restitué step) */}
      {isRestitue && (
        <RatingBlock
          hoveredStar={hoveredStar}
          setHoveredStar={setHoveredStar}
          selectedRating={selectedRating}
          setSelectedRating={setSelectedRating}
          ordreId={ordreId}
          previewMode={previewMode}
        />
      )}

      {/* Info card */}
      <div className="flex flex-col gap-[15px] p-[10px] bg-[#FAFAFA] border border-[#E4E4E4] rounded-[10px]">
        {["carte_grise_manquante", "acompte_manquant", "en_attente_depot", "vehicule_depose"].includes(data.tracking_step)
          ? <InfoRow label="Date de dépôt du véhicule" value={data.deposit_date || "À définir"} large />
          : <InfoRow label="Date de restitution" value={data.restitution_date || "À définir"} large />
        }
        {data.org_name && (
          <InfoRow label="Garage" value={data.org_name} />
        )}
        <CopyableAddress value={data.org_address || "—"} />
        <InfoRow
          label="Coordonnées"
          value={[data.org_phone, data.org_email].filter(Boolean).join("\n") || "—"}
        />
      </div>

      {/* "Poser une question" button */}
      <button
        onClick={() => !previewMode && alert("Fonctionnalité à venir")}
        className="flex items-center justify-center gap-[10px] py-[15px] bg-white border border-[#5EEAD4] rounded-[10px] shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:bg-neutral-50 transition-colors"
      >
        <span className="font-bold text-[15px] leading-[100%] text-black">
          Poser une question
        </span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#43E4CB">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 0 0 .613.613l4.458-1.495A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.34 0-4.512-.753-6.277-2.03l-.44-.33-3.095 1.038 1.038-3.095-.33-.44A9.965 9.965 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
        </svg>
      </button>

      {/* Request summary table */}
      <div className="flex flex-col border border-[#E6E6E6] rounded-[15px] bg-[#FBFCFF] py-[10px] overflow-hidden">
        {summaryRows.map((row, i) => (
          <div key={row.label}>
            <div className="flex justify-between items-start px-[10px] py-[1px]">
              <span className="text-[10px] font-medium leading-[15px] text-[#D9D9D9]">
                {row.label}
              </span>
              <span className="text-[10px] font-medium leading-[15px] text-[#A1A1A1] text-right">
                {row.value}
              </span>
            </div>
            {i < summaryRows.length - 1 && (
              <div className="border-b border-[#D9D9D9] my-[5px]" />
            )}
          </div>
        ))}
      </div>

      {/* Mobility options */}
      <div className="flex flex-col gap-[2px] pt-[10px]">
        {MOBILITY_OPTIONS.map((opt) => {
          const isSelected = data.mobility_option === opt.key;
          return (
            <label key={opt.key} className="flex items-center gap-[10px] cursor-default">
              <div
                className="w-[14px] h-[14px] rounded-[3px] border flex items-center justify-center shrink-0"
                style={{
                  background: isSelected ? "#A1A1A1" : "transparent",
                  borderColor: isSelected ? "#A1A1A1" : "#D9D9D9",
                }}
              >
                {isSelected && (
                  <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
                    <path d="M1 2.5L2.5 4L6 1" stroke="white" strokeWidth="1" />
                  </svg>
                )}
              </div>
              <span
                className="text-[10px] font-medium leading-[15px]"
                style={{ color: isSelected ? "#A1A1A1" : "#D9D9D9" }}
              >
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>

      {/* Tire options */}
      <div className="flex flex-col gap-[2px] pt-[10px] pb-5">
        {TIRE_OPTIONS.map((opt) => {
          const isSelected = data.tire_option === opt.key;
          return (
            <label key={opt.key} className="flex items-center gap-[10px] cursor-default">
              <div
                className="w-[14px] h-[14px] rounded-[3px] border flex items-center justify-center shrink-0"
                style={{
                  background: isSelected ? "#A1A1A1" : "transparent",
                  borderColor: isSelected ? "#A1A1A1" : "#D9D9D9",
                }}
              >
                {isSelected && (
                  <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
                    <path d="M1 2.5L2.5 4L6 1" stroke="white" strokeWidth="1" />
                  </svg>
                )}
              </div>
              <span
                className="text-[10px] font-medium leading-[15px]"
                style={{ color: isSelected ? "#A1A1A1" : "#D9D9D9" }}
              >
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────

const RATING_LABELS = ["", "Mauvais", "Décevant", "Correct", "Très bien", "Excellent"];

function RatingBlock({
  hoveredStar,
  setHoveredStar,
  selectedRating,
  setSelectedRating,
  ordreId,
  previewMode,
}: {
  hoveredStar: number;
  setHoveredStar: (v: number) => void;
  selectedRating: number;
  setSelectedRating: (v: number) => void;
  ordreId?: string;
  previewMode?: boolean;
}) {
  const [justRated, setJustRated] = useState(false);
  const [popStar, setPopStar] = useState(0);

  const handleRate = (star: number) => {
    if (previewMode) return;
    setSelectedRating(star);
    setPopStar(star);
    setJustRated(true);
    setTimeout(() => setJustRated(false), 600);
    setTimeout(() => setPopStar(0), 400);
    if (ordreId) submitTrackingRating(ordreId, star).catch(() => {});
  };

  return (
    <div className="flex flex-col items-center gap-3 py-8">
      <span className="text-[15px] font-semibold text-black">
        Comment s'est passée votre expérience ?
      </span>
      <div className="flex gap-5 py-3">
        {[1, 2, 3, 4, 5].map((star) => {
          const active = star <= (hoveredStar || selectedRating);
          const popping = popStar >= star && justRated;
          return (
            <button
              key={star}
              onMouseEnter={() => setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar(0)}
              onClick={() => handleRate(star)}
              className="transition-transform duration-200"
              style={{
                transform: popping
                  ? `scale(${1.4 - (star - 1) * 0.05})`
                  : active ? "scale(1)" : "scale(0.95)",
                transitionDelay: popping ? `${(star - 1) * 50}ms` : "0ms",
              }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill={active ? "#5C6BFF" : "none"} stroke={active ? "#5C6BFF" : "#D9D9D9"} strokeWidth="1.5" style={{ transition: "fill 150ms, stroke 150ms", transitionDelay: `${(star - 1) * 50}ms` }}>
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </button>
          );
        })}
      </div>
      <div className="h-5">
        {(hoveredStar > 0 || selectedRating > 0) && (
          <span className="text-[13px] font-medium text-[#5C6BFF]">
            {RATING_LABELS[hoveredStar || selectedRating]}
          </span>
        )}
      </div>
    </div>
  );
}

function CopyableAddress({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);

  const openInMaps = useCallback(() => {
    const q = encodeURIComponent(value);
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank");
  }, [value]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [value]);

  const onPointerDown = () => {
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      openInMaps();
    }, 600);
  };

  const onPointerUp = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  if (value === "—") {
    return <InfoRow label="Adresse" value="—" />;
  }

  return (
    <div className="flex flex-col gap-[2px]">
      <span className="text-[11px] font-medium leading-4 text-[#A1A1A1]">Adresse</span>
      <div
        className="flex items-start justify-between gap-2 cursor-pointer select-none"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={() => { if (!didLongPress.current) copyToClipboard(); }}
      >
        <span className="font-medium leading-4 text-[#5C6BFF] whitespace-pre-line" style={{ fontSize: 11 }}>
          {value}
        </span>
        <div className="shrink-0 mt-[1px]">
          {copied ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#5C6BFF">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#A1A1A1">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
            </svg>
          )}
        </div>
      </div>
      <span className="text-[9px] text-[#D9D9D9] leading-3">
        {copied ? "Copié !" : "Appui long pour ouvrir dans Maps"}
      </span>
    </div>
  );
}

function InfoRow({ label, value, large }: { label: string; value: string; large?: boolean }) {
  return (
    <div className="flex flex-col gap-[2px]">
      <span className="text-[11px] font-medium leading-4 text-[#A1A1A1]">{label}</span>
      <span
        className={`font-${large ? "bold" : "medium"} leading-${large ? "[100%]" : "4"} text-[#5C6BFF] whitespace-pre-line`}
        style={{ fontSize: large ? 20 : 11 }}
      >
        {value}
      </span>
    </div>
  );
}
